import { AnalyticsBrowser } from '@segment/analytics-next';
import { Injectable } from '@angular/core';
import { withOneTrust } from '@segment/analytics-consent-wrapper-onetrust';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../../environments/environment';

const CID_COOKIE_NAME = 'cid';
const PROSPECT_ID_COOKIE_NAME = 'prospect__id';

@Injectable({
  providedIn: 'root',
})
export class SegmentJsAnalyticsService {
  private analytics: AnalyticsBrowser | null = null;
  private isLoaded = false;

  constructor(private readonly cookieService: CookieService) {
    if (environment.segmentWriteKey) {
      const browser = new AnalyticsBrowser();
      // withOneTrust only swaps in a consent-aware load() method; it does not read
      // consent or fetch anything until load() is called. Wrapping here (rather than
      // in init) keeps the instance available immediately so page()/identify() calls
      // made before load() are buffered by analytics-next instead of dropped.
      this.analytics = environment.oneTrustDomainScript ? withOneTrust(browser) : browser;
    }
  }

  /**
   * Triggers the deferred Segment load (which is when OneTrust consent is read).
   * Must be called AFTER OneTrust has the data subject id set, so consent resolves
   * against the correct D-${dpid} subject. Safe to call more than once.
   */
  public init(): void {
    if (this.isLoaded || !this.analytics) {
      return;
    }

    this.isLoaded = true;
    this.analytics.load({ writeKey: environment.segmentWriteKey! });
  }

  public async setupSegmentJsAnalytics(): Promise<void> {
    if (!this.analytics) {
      return;
    }

    try {
      const dpid = this.cookieService.get(PROSPECT_ID_COOKIE_NAME) || undefined;

      const properties: Record<string, string | undefined> = {
        dpid,
      };

      await this.analytics.page(properties);
    } catch (error) {
      console.error('Segment.js analytics setup failed:', error);
    }
  }

  public async identify(): Promise<void> {
    if (!this.analytics) {
      return;
    }

    try {
      const cid = this.cookieService.get(CID_COOKIE_NAME) || undefined;
      const dpid = this.cookieService.get(PROSPECT_ID_COOKIE_NAME) || undefined;

      await this.analytics.identify(cid);

      if (dpid) {
        this.analytics.instance.user().anonymousId(dpid);
      }

      if (cid) {
        this.analytics.instance.user().id(cid);
      }
    } catch (error) {
      console.error('Segment.js analytics identify failed:', error);
    }
  }
}
